<template>
    <div
        v-if="availStatusObj.statusText"
        class="d-flex align-center"
        :class="direction === 'horizontal' ? 'justify-space-between' : 'flex-column justify-center'"
    >
        <v-chip
            :color="useGetStatusColor(availStatusObj.statusText)"
            variant="outlined"
            :density="(direction === 'vertical' && !showEstShipDate) ? 'default' : 'compact'"
            :size="size"
            label
        >
            {{ availStatusObj.statusText }}
        </v-chip>
        <span
            v-if="showEstShipDate"
            :class="size === 'small' ? 'font-size-8' : ''"
        >
            <span v-if="!compact">Estimated </span><strong>{{ statusObj.shipDate }}</strong>
        </span>
    </div>
</template>

<script lang="ts" setup>
    import type {
        GenericObject
    } from '~/types';
    import {
        ProductAvailabilityStatus
    } from '~/types';
    import {
        useGetStatusColor,
        useProductAvailabilityStatus
    } from '~/composables';

    interface LocalStatusObj {
        status: Nullable<string>;
        shipDate: Nullable<string>;
    }

    interface ObjectWithStatus extends GenericObject<any> {
        status: Nullable<string>;
    };

    interface ObjectWithStatusAndShipDate extends ObjectWithStatus {
        isInStock: boolean;
        shipDate: Nullable<string>;
    };

    const props = withDefaults(
        defineProps<{
            currentSku: ObjectWithStatus;
            augmentStatus?: Nilish<ObjectWithStatusAndShipDate[]>;
            size?: Undefinable<string | number>;
            direction?: 'horizontal' | 'vertical';
            compact?: boolean;
        }>(),
        {
            size: 'x-large',
            direction: 'horizontal'
        }
    );

    const statusObj: LocalStatusObj = reactive({
        status: props.currentSku.status,
        shipDate: props.currentSku.shipDate
    });

    const availStatusObj = reactive({
        statusText: null,
        canShowEstShipDate: false
    });

    const showEstShipDate = computed<boolean>(() => {
        if (!statusObj.shipDate) {
            return false;
        }

        return availStatusObj.canShowEstShipDate;
    });

    function setStatusObj(): void {
        const obj = useProductAvailabilityStatus(statusObj.status as ProductAvailabilityStatus);
        Object.assign(availStatusObj, obj);
    }

    function statusUpdateHandler(
        newStatus: Nilish<ObjectWithStatusAndShipDate[]>
    ): void {
        if (!newStatus?.length) {
            setDefaultStatusAndShipDate();
            return;
        }

        const isInStock = new Set();
        const sorted = newStatus.slice(0).sort((a, b) => {
            isInStock.add(a.isInStock);
            isInStock.add(b.isInStock);
            const d1 = a.shipDate, d2 = b.shipDate;
            if (!d1) return -1;
            if (!d2) return 1;
            if (d1 < d2) return -1;
            if (d1 > d2) return 1;
            return 0;
        });

        if (!isInStock.has(false)) {
            setDefaultStatusAndShipDate();
            return;
        }

        const items = sorted.reverse();
        const latestShipObj = items.find((o, i) => {
            const { shipDate, status } = o;

            if (shipDate && status) {
                const nextItem = items[i + 1];

                if (status !== 'preorder') return o;

                if (nextItem) {
                    const { shipDate: nextShipDate, status: nextStatus } = nextItem;

                    if (nextShipDate && nextStatus && shipDate === nextShipDate && status !== nextStatus) {
                        return;
                    }

                    return o;
                }
            }
        });

        if (latestShipObj) {
            const { shipDate, status } = latestShipObj;
            statusObj.shipDate = shipDate;
            statusObj.status = status;
            return;
        }

        setDefaultStatusAndShipDate();
    }

    function setDefaultStatusAndShipDate(): void {
        const newSku = props.currentSku;
        statusObj.status = newSku?.status || null;
        statusObj.shipDate = newSku?.shipDate || null;
    }

    const unWatchSku = watch(
        () => props.currentSku,
        (newSku) => {
            setDefaultStatusAndShipDate();
            setStatusObj();
        },
        {
            immediate: true
        }
    );

    const unWatchStatusObj = watch(
        statusObj,
        setStatusObj,
        {
            immediate: true,
            deep: true
        }
    );

    const unWatchAugStatus = watch(
        () => props.augmentStatus,
        statusUpdateHandler,
        {
            immediate: true,
            deep: true
        }
    );

    onBeforeUnmount(() => {
        unWatchStatusObj();
        unWatchSku();
        unWatchAugStatus();
    });
</script>

<style lang="scss"></style>
